// App-level error boundary for the Control Center routes. Without one, ANY
// render throw (e.g. an unexpected null in a table cell) makes React 18
// unmount the entire tree — every operator gets an unrecoverable white screen
// (arch review P0-1). The boundary contains the crash to the routed content:
// the header/nav shell stays alive, and the fallback offers reload/home.
//
// Mount it keyed by route path so navigating away automatically resets the
// error state (a crash on one screen must not brick the others).

import React from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Button, Result } from "src/assets/imports/antdComponents";

interface BoundaryProps {
	t: TFunction;
	children: React.ReactNode;
}

interface BoundaryState {
	hasError: boolean;
}

class Boundary extends React.Component<BoundaryProps, BoundaryState> {
	state: BoundaryState = { hasError: false };

	static getDerivedStateFromError(): BoundaryState {
		return { hasError: true };
	}

	componentDidCatch(error: Error, info: React.ErrorInfo): void {
		// Console only — no remote error sink is wired in this admin build.
		console.error("KCC screen crashed:", error, info.componentStack);
	}

	render(): React.ReactNode {
		const { children, t } = this.props;
		if (!this.state.hasError) return <>{children}</>;
		return (
			<div className="flex flex-col justify-center items-center h-full">
				<Result
					status="500"
					title={t("Something went wrong")}
					subTitle={t("This screen hit an unexpected error. Your data is safe — reload to continue.")}
					extra={
						<div className="flex items-center justify-center gap-3">
							<Button type="primary" className="kcc-modal__primary-btn" onClick={() => window.location.reload()}>
								{t("Reload")}
							</Button>
							<Button
								className="kcc-modal__ghost-btn"
								onClick={() => {
									window.location.href = "/tenants";
								}}>
								{t("Back to tenants")}
							</Button>
						</div>
					}
				/>
			</div>
		);
	}
}

const KccErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { t } = useTranslation();
	return <Boundary t={t}>{children}</Boundary>;
};

export default KccErrorBoundary;
